import React, {
    createContext,
    useCallback,
    useRef,
    useState,
    ReactNode,
} from 'react';
import HttpCotinService from '@/services/HttpCotinService';
import {
    getPortalUrl,
    normalizeLoginQuerystring,
    redirectToPortal,
    shouldSkipAuthLogin,
} from '@/core/configs/auth/auth.config';
import { hasValidLoginAD, normalizeUsuarioSessao } from '@/core/configs/auth/auth.utils';
import { Usuario } from './models/usuario.model';
import type { AuthLocalSelecao, PerfilAcessoLocal } from './models/perfil-acesso-local.model';

interface AuthContextType {
    usuarioSessao: Usuario | null;
    isLoading: boolean;
    login: (querystring: string) => Promise<Usuario>;
    consultarLocal: (login: string) => Promise<PerfilAcessoLocal[]>;
    loginLocal: (login: string, selecao: AuthLocalSelecao) => Promise<Usuario>;
    logout: (options?: { redirectToPortal?: boolean }) => Promise<void>;
    getSessionUser: (options?: { logoutIfMissing?: boolean }) => Promise<Usuario>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [usuarioSessao, setUsuarioSessao] = useState<Usuario | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const usuarioDaSessaoRef = useRef<Usuario | null>(null);
    const logoutEmProgressoRef = useRef(false);
    const loginPromisesRef = useRef<Map<string, Promise<Usuario>>>(new Map());

    const applySessionUser = useCallback((usuario: Usuario): Usuario => {
        const normalized = normalizeUsuarioSessao(usuario);

        if (hasValidLoginAD(normalized)) {
            usuarioDaSessaoRef.current = normalized;
            setUsuarioSessao(normalized);
        } else {
            usuarioDaSessaoRef.current = null;
            setUsuarioSessao(null);
        }

        return normalized;
    }, []);

    const updateSessionUser = useCallback(async (): Promise<Usuario> => {
        const response = await HttpCotinService.Get<Usuario>('Autenticacao/UserSession');
        return applySessionUser(response);
    }, [applySessionUser]);

    const logout = useCallback(async (options?: { redirectToPortal?: boolean }): Promise<void> => {
        if (logoutEmProgressoRef.current) {
            return;
        }
        logoutEmProgressoRef.current = true;

        try {
            await HttpCotinService.Get<void>('Autenticacao/Logout');
        } catch (error) {
            console.warn('AuthService: falha ao encerrar sessão no servidor.', error);
        }

        usuarioDaSessaoRef.current = null;
        setUsuarioSessao(null);

        try {
            sessionStorage.removeItem('lastHttpError');
        } catch {
            /* ignore */
        }

        const deveRedirecionar = options?.redirectToPortal !== false;
        if (deveRedirecionar) {
            const portalUrl = getPortalUrl();
            const target = window.top && window.top !== window.self ? window.top : window.self;

            queueMicrotask(() => {
                target.location.replace(portalUrl);
            });
        }

        logoutEmProgressoRef.current = false;
    }, []);

    const getSessionUser = useCallback(
        async (options?: { logoutIfMissing?: boolean }): Promise<Usuario> => {
            if (usuarioDaSessaoRef.current && hasValidLoginAD(usuarioDaSessaoRef.current)) {
                return usuarioDaSessaoRef.current;
            }

            setIsLoading(true);

            try {
                const response = await HttpCotinService.Get<Usuario>('Autenticacao/UserSession');
                const normalized = normalizeUsuarioSessao((response || {}) as Usuario);

                if (!hasValidLoginAD(normalized)) {
                    if (options?.logoutIfMissing !== false) {
                        await logout();
                    }
                    throw new Error('Usuário não autenticado.');
                }

                return applySessionUser(normalized);
            } finally {
                setIsLoading(false);
            }
        },
        [applySessionUser, logout]
    );

    const executarLogin = useCallback(
        async (loginUri: string, redirecionarPortalEmErro: boolean): Promise<Usuario> => {
            setIsLoading(true);

            try {
                try {
                    await HttpCotinService.Get<void>('Autenticacao/Logout');
                } catch {
                    /* ignora falha no logout pré-login */
                }

                usuarioDaSessaoRef.current = null;
                setUsuarioSessao(null);

                const authResult = await HttpCotinService.Post<Usuario>(loginUri, null);
                const normalizedAuth = normalizeUsuarioSessao((authResult || {}) as Usuario);

                try {
                    const sessionFromApi = await updateSessionUser();
                    if (hasValidLoginAD(sessionFromApi)) {
                        return sessionFromApi;
                    }
                } catch (sessionError) {
                    console.warn(
                        'AuthService: UserSession após login falhou; usando resposta do Login.',
                        sessionError
                    );
                }

                if (hasValidLoginAD(normalizedAuth)) {
                    return applySessionUser(normalizedAuth);
                }

                return normalizedAuth;
            } catch (error) {
                console.error('AuthService - Erro no login:', error);
                if (redirecionarPortalEmErro) {
                    redirectToPortal(
                        error instanceof Error ? error.message : 'Erro no login'
                    );
                }
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [applySessionUser, updateSessionUser]
    );

    const login = useCallback(
        async (querystring: string): Promise<Usuario> => {
            if (shouldSkipAuthLogin()) {
                return {} as Usuario;
            }

            const normalizedQuery = normalizeLoginQuerystring(querystring);
            if (!normalizedQuery) {
                throw new Error('Querystring de autenticação é obrigatória.');
            }

            const inFlight = loginPromisesRef.current.get(normalizedQuery);
            if (inFlight) {
                return inFlight;
            }

            const encodedQuerystring = encodeURIComponent(normalizedQuery);
            const loginTask = executarLogin(
                `Autenticacao/Login?querystring=${encodedQuerystring}`,
                true
            );

            loginPromisesRef.current.set(normalizedQuery, loginTask);

            try {
                return await loginTask;
            } finally {
                loginPromisesRef.current.delete(normalizedQuery);
            }
        },
        [executarLogin]
    );

    /** Consulta perfis/unidades/sistemas (passo Consultar do AutenticaDEV). */
    const consultarLocal = useCallback(async (login: string): Promise<PerfilAcessoLocal[]> => {
        const normalizedLogin = (login ?? '').trim();
        if (!normalizedLogin) {
            throw new Error('Informe o login AD.');
        }

        const response = await HttpCotinService.Get<PerfilAcessoLocal[]>(
            `Autenticacao/ConsultarLocal?login=${encodeURIComponent(normalizedLogin)}`
        );

        const lista = Array.isArray(response) ? response : [];
        return lista.map((item) => {
            const raw = item as PerfilAcessoLocal & {
                SistemaId?: number;
                Sistema?: string;
                GrupoId?: number;
                Grupo?: string;
                UnidadeId?: number;
                Unidade?: string;
                TipoAcessoId?: number;
                TipoPerfilAcesso?: string;
            };
            return {
                sistemaId: raw.sistemaId ?? raw.SistemaId ?? 0,
                sistema: raw.sistema ?? raw.Sistema ?? '',
                grupoId: raw.grupoId ?? raw.GrupoId ?? 0,
                grupo: raw.grupo ?? raw.Grupo ?? '',
                unidadeId: raw.unidadeId ?? raw.UnidadeId ?? 0,
                unidade: raw.unidade ?? raw.Unidade ?? '',
                tipoAcessoId: raw.tipoAcessoId ?? raw.TipoAcessoId ?? 0,
                tipoPerfilAcesso: raw.tipoPerfilAcesso ?? raw.TipoPerfilAcesso ?? '',
            };
        });
    }, []);

    /** Login local (Development). Exige login + unidade + CPF/CNPJ do representante; SistemaId fixo no BFF (AuthLocal.SistemaId). */
    const loginLocal = useCallback(
        async (login: string, selecao: AuthLocalSelecao): Promise<Usuario> => {
            const normalizedLogin = (login ?? '').trim();
            if (!normalizedLogin) {
                throw new Error('Informe o login AD.');
            }
            if (!selecao?.unidadeId) {
                throw new Error('Selecione a unidade.');
            }

            const loginRepresentante = (selecao.loginRepresentante ?? '').replace(/\D/g, '');
            if (loginRepresentante.length !== 11 && loginRepresentante.length !== 14) {
                throw new Error('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) do representante.');
            }

            const key = `local:${normalizedLogin}:${selecao.unidadeId}:${loginRepresentante}`;
            const inFlight = loginPromisesRef.current.get(key);
            if (inFlight) {
                return inFlight;
            }

            const params = new URLSearchParams({
                login: normalizedLogin,
                unidadeId: String(selecao.unidadeId),
                loginRepresentante,
            });
            if (selecao.tipoAcessoId && selecao.tipoAcessoId > 0) {
                params.set('tipoAcessoId', String(selecao.tipoAcessoId));
            }

            const loginTask = executarLogin(`Autenticacao/LoginLocal?${params.toString()}`, false);
            loginPromisesRef.current.set(key, loginTask);

            try {
                return await loginTask;
            } finally {
                loginPromisesRef.current.delete(key);
            }
        },
        [executarLogin]
    );

    return (
        <AuthContext.Provider
            value={{
                usuarioSessao,
                isLoading,
                login,
                consultarLocal,
                loginLocal,
                logout,
                getSessionUser,
            }}>
            {children}
        </AuthContext.Provider>
    );
};
